"use client";

import { useState, useEffect } from "react";
import { useRouter } from "next/navigation";
import { clog } from "@/lib/client-log";
import EliminatedNameModal from "./eliminated-name-modal";
import { placeFuturesBet } from "../actions/kudos-client";
import { calcFuturesOdds } from "@/lib/odds";

// ── Types ─────────────────────────────────────────────────────────────────────

interface MatchHistoryEntry {
  matchNumber: number;
  round: number;
  won: boolean;
  tied: boolean;
  opponent: string;
  isTieBreaker: boolean;
  isRematch: boolean;
  winnerStreak: number;
  myVotes: number;
  opponentVotes: number;
  commentary: string | null;
}

interface NameData {
  id: string;
  name: string;
  meaning: string | null;
  origin: string | null;
  notes: string | null;
  winCount: number;
  lossCount: number;
  tieCount: number;
  winStreak: number;
  eliminated: boolean;
  resurrected: boolean;
  lcKVoteCount: number;
  status: string;
  matchHistory: MatchHistoryEntry[];
  lcKEligible?: boolean;
  lckOpen?: boolean;
}

// ── Futures bet section inside PlayerCard ─────────────────────────────────────

function FuturesBetSection({
  nameId,
  nameName,
  odds,
  userBalance,
  existingBet,
}: {
  nameId: string;
  nameName: string;
  odds: number;
  userBalance: number;
  existingBet: { wager: number; odds: number } | null;
}) {
  const [bet, setBet]         = useState(existingBet);
  const [wager, setWager]     = useState("");
  const [status, setStatus]   = useState<"idle" | "placing" | "error">("idle");
  const [errMsg, setErrMsg]   = useState("");

  const parsed    = parseInt(wager, 10);
  const potential = parsed > 0 ? Math.floor(parsed * odds) : null;

  // Already have a bet — show the badge
  if (bet) {
    const pay = Math.floor(bet.wager * bet.odds);
    return (
      <div>
        <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">
          Your futures bet
        </h3>
        <div className="rounded-xl bg-amber-50 border border-amber-200 px-4 py-3 flex items-center justify-between gap-3">
          <div>
            <div className="text-sm font-black text-amber-700">
              🎰 {bet.wager} K @ {bet.odds.toFixed(1)}×
            </div>
            <div className="text-xs text-amber-500 mt-0.5">
              Potential payout: <span className="font-black">{pay.toLocaleString()} K</span>
            </div>
          </div>
          <span className="text-xs font-bold text-amber-400 bg-amber-100 rounded-full px-2 py-0.5">Open</span>
        </div>
      </div>
    );
  }

  async function handleBet() {
    if (!parsed || parsed < 1) return;
    clog.info("player-card-bet", "placing futures bet", { nameId, nameName, wager: parsed, odds });
    setStatus("placing");
    setErrMsg("");
    try {
      const res = await placeFuturesBet(nameId, parsed);
      if (res.success && res.odds) {
        clog.info("player-card-bet", "bet placed", { nameId, wager: parsed, odds: res.odds });
        setBet({ wager: parsed, odds: res.odds });
        setWager("");
        setStatus("idle");
      } else {
        clog.warn("player-card-bet", `bet rejected: ${res.error}`, { nameId });
        setStatus("error");
        setErrMsg(res.error ?? "Bet failed");
        setTimeout(() => setStatus("idle"), 3500);
      }
    } catch (err) {
      clog.error("player-card-bet", "placeFuturesBet threw", {
        error: err instanceof Error ? err.message : String(err),
      });
      setStatus("error");
      setErrMsg("Something went wrong");
      setTimeout(() => setStatus("idle"), 3500);
    }
  }

  return (
    <div>
      <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">
        Futures bet
      </h3>
      <div className="rounded-xl bg-purple-50 border border-purple-100 px-4 py-3 space-y-3">
        <div className="flex items-center justify-between text-xs">
          <span className="text-purple-500">
            Odds to win championship:&nbsp;
            <span className="font-black text-purple-700">{odds.toFixed(1)}×</span>
          </span>
          <span className="text-purple-400">
            Balance:&nbsp;<span className="font-black text-purple-700">{userBalance.toLocaleString()} K</span>
          </span>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="number"
            min={1}
            max={userBalance}
            value={wager}
            onChange={(e) => setWager(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") handleBet(); }}
            placeholder="Wager"
            className="flex-1 min-w-0 rounded-xl border-2 border-purple-200 px-3 py-2 text-sm font-black text-purple-800 focus:border-purple-500 focus:outline-none"
          />
          <button
            onClick={handleBet}
            disabled={status === "placing" || !parsed || parsed < 1 || parsed > userBalance}
            className="shrink-0 rounded-xl bg-purple-700 px-4 py-2 text-sm font-black text-white hover:bg-purple-600 disabled:opacity-40 disabled:cursor-not-allowed transition select-none"
            style={{ WebkitUserSelect: "none", userSelect: "none" }}
          >
            {status === "placing" ? "…" : "Bet"}
          </button>
        </div>

        {potential !== null && potential > 0 && (
          <div className="text-xs text-center text-green-700 font-bold">
            Win → <span className="font-black">{potential.toLocaleString()} K</span>
          </div>
        )}

        {status === "error" && (
          <div className="text-xs font-bold text-red-500 text-center">{errMsg}</div>
        )}
      </div>
    </div>
  );
}

// ── Player card modal (active / resurrected names) ────────────────────────────

function PlayerCard({
  initial,
  onClose,
  lckRequired,
  futuresOdds,
  userBalance,
  existingBet,
}: {
  initial: NameData;
  onClose: () => void;
  lckRequired: number;
  futuresOdds?: number;
  userBalance?: number;
  existingBet?: { wager: number; odds: number } | null;
}) {
  const [n, setN]                   = useState<NameData>(initial);
  const [refreshing, setRefreshing] = useState(true);
  // Live futures odds — recomputed from freshly-fetched stats + pool size on
  // open instead of trusting the page-load-time `futuresOdds` prop, which can
  // go stale in a long-lived tab the same way the LCK vote threshold did.
  const [liveOdds, setLiveOdds]     = useState<number | undefined>(futuresOdds);

  useEffect(() => {
    let cancelled = false;
    async function fetchFresh() {
      clog.debug("player-card", `fetching fresh data for "${initial.name}"`, { id: initial.id });
      try {
        const res = await fetch(`/api/name?id=${initial.id}`);
        if (!res.ok) { clog.warn("player-card", `/api/name returned ${res.status}`); return; }
        const fresh: NameData & { totalActiveNames?: number } = await res.json();
        if (!cancelled) {
          setN(fresh);
          if (typeof fresh.totalActiveNames === "number" && !fresh.eliminated) {
            setLiveOdds(calcFuturesOdds(fresh, fresh.totalActiveNames));
          }
        }
      } catch (err) {
        clog.error("player-card", `fetch threw`, { error: err instanceof Error ? err.message : String(err) });
      } finally {
        if (!cancelled) setRefreshing(false);
      }
    }
    fetchFresh();
    return () => { cancelled = true; };
  }, [initial.id, initial.name]);

  const total  = n.winCount + n.lossCount + n.tieCount;
  const winPct = total > 0 ? Math.round((n.winCount / total) * 100) : null;
  const recordStr = n.tieCount > 0
    ? `${n.winCount}–${n.tieCount}–${n.lossCount}`
    : `${n.winCount}–${n.lossCount}`;
  const statusLabel =
    n.status === "CHAMPION"    ? { text: "Champion",    cls: "bg-yellow-100 text-yellow-800" } :
    n.status === "RESURRECTED" ? { text: "Resurrected", cls: "bg-orange-100 text-orange-700" } :
    n.eliminated               ? { text: "Eliminated",  cls: "bg-red-50 text-red-500"        } :
                                 { text: "Active",       cls: "bg-green-50 text-green-700"    };

  const showBetting = !n.eliminated && liveOdds !== undefined && userBalance !== undefined;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(15,5,32,0.7)" }}
      onClick={onClose}
    >
      <div className="w-full max-w-md rounded-3xl bg-white shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="bg-gradient-to-br from-[#1e0b4a] to-[#3b0764] px-6 pt-6 pb-5">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-3xl font-black text-white leading-tight">{n.name}</h2>
              {n.origin ? (
                <p className="text-purple-300 text-sm mt-0.5">{n.origin}</p>
              ) : refreshing ? (
                <p className="text-purple-500 text-xs mt-0.5 animate-pulse">Loading…</p>
              ) : null}
            </div>
            <div className="flex flex-col items-end gap-1.5 shrink-0">
              <span className={`rounded-full px-2.5 py-0.5 text-xs font-black ${statusLabel.cls}`}>{statusLabel.text}</span>
              <button onClick={onClose} className="text-purple-400 hover:text-white text-xl leading-none mt-1 select-none" style={{ WebkitUserSelect: "none", userSelect: "none" }} aria-label="Close">✕</button>
            </div>
          </div>
          <div className="flex gap-3 mt-4">
            {[
              { label: "Record", value: recordStr },
              { label: "Win %",  value: winPct !== null ? `${winPct}%` : "—" },
              { label: "Streak", value: n.winStreak >= 2 ? `W${n.winStreak}` : "—" },
            ].map(({ label, value }) => (
              <div key={label} className="flex-1 rounded-xl bg-white/10 px-3 py-2 text-center">
                <div className="text-white font-black text-lg leading-tight">{value}</div>
                <div className="text-purple-300 text-[10px] uppercase tracking-wider mt-0.5">{label}</div>
              </div>
            ))}
          </div>
          {n.tieCount > 0 && (
            <div className="mt-3 rounded-xl bg-yellow-400/15 border border-yellow-400/30 px-3 py-2 text-center">
              <span className="text-yellow-300 text-xs font-bold">⚡ {n.tieCount} deadlock{n.tieCount !== 1 ? "s" : ""} — sent back to the pool</span>
            </div>
          )}
        </div>

        {/* Body */}
        <div className="px-6 py-5 space-y-5 max-h-[60vh] overflow-y-auto">
          {n.meaning ? (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">Meaning</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{n.meaning}</p>
            </div>
          ) : refreshing ? (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">Meaning</h3>
              <div className="h-4 bg-purple-50 rounded animate-pulse w-3/4" />
              <div className="h-4 bg-purple-50 rounded animate-pulse w-1/2 mt-1.5" />
            </div>
          ) : null}

          {n.notes && (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-1.5">Why we love it</h3>
              <p className="text-gray-700 text-sm leading-relaxed italic">"{n.notes}"</p>
            </div>
          )}

          {/* Futures bet — only shown when odds + balance are passed in (roster page) */}
          {showBetting && (
            <FuturesBetSection
              nameId={n.id}
              nameName={n.name}
              odds={liveOdds!}
              userBalance={userBalance}
              existingBet={existingBet ?? null}
            />
          )}

          {n.eliminated && !n.resurrected && n.lcKVoteCount > 0 && (
            <div>
              <h3 className="text-[10px] font-black text-amber-500 uppercase tracking-widest mb-1.5">Last Chance Kitchen</h3>
              <div className="flex items-center gap-2">
                <div className="flex-1 h-2 bg-amber-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${Math.min(100, Math.round((n.lcKVoteCount / lckRequired) * 100))}%` }} />
                </div>
                <span className="text-xs font-black text-amber-600">{n.lcKVoteCount}/{lckRequired}</span>
              </div>
            </div>
          )}

          {n.matchHistory.length > 0 && (
            <div>
              <h3 className="text-[10px] font-black text-purple-400 uppercase tracking-widest mb-2">Match history</h3>
              <div className="space-y-2">
                {[...n.matchHistory].reverse().map((m) => {
                  const isTied    = m.tied;
                  const rowCls    = isTied ? "bg-amber-50 border border-amber-200" : m.won ? "bg-green-50 border border-green-100" : "bg-red-50 border border-red-100";
                  const letterCls = isTied ? "text-amber-600" : m.won ? "text-green-600" : "text-red-400";
                  const letter    = isTied ? "T" : m.won ? "W" : "L";
                  const totalVotes = m.myVotes + m.opponentVotes;
                  const myPct     = totalVotes > 0 ? Math.round((m.myVotes / totalVotes) * 100) : 50;
                  const barFill   = isTied ? "bg-amber-400" : m.won ? "bg-green-400" : "bg-red-300";
                  const barTrack  = isTied ? "bg-amber-100" : m.won ? "bg-green-100" : "bg-red-100";
                  return (
                    <div key={m.matchNumber} className={`rounded-xl px-3 py-2.5 ${rowCls}`}>
                      <div className="flex items-center gap-2">
                        <span className={`font-black w-5 text-center text-sm ${letterCls}`}>{letter}</span>
                        <span className="flex-1 text-gray-700 text-sm">vs <span className="font-bold">{m.opponent}</span>{isTied && <span className="ml-1.5 text-[10px] text-amber-600 font-semibold">deadlock</span>}</span>
                        <div className="flex items-center gap-1 shrink-0">
                          {m.isTieBreaker && <span className="rounded bg-yellow-100 px-1.5 text-[9px] font-black text-yellow-700">TB</span>}
                          {m.isRematch    && <span className="rounded bg-orange-100 px-1.5 text-[9px] font-black text-orange-600">Rematch</span>}
                          {!isTied && m.won && m.winnerStreak >= 2 && <span className="rounded bg-purple-100 px-1.5 text-[9px] font-black text-purple-600">W{m.winnerStreak}</span>}
                          <span className="text-[10px] text-gray-400">Rd {m.round}</span>
                        </div>
                      </div>
                      {totalVotes > 0 && (
                        <div className="mt-2 flex items-center gap-2">
                          <span className={`text-[11px] font-black tabular-nums w-4 text-right ${letterCls}`}>{m.myVotes}</span>
                          <div className={`flex-1 h-1.5 rounded-full overflow-hidden ${barTrack}`}>
                            <div className={`h-full rounded-full transition-all ${barFill}`} style={{ width: `${myPct}%` }} />
                          </div>
                          <span className="text-[11px] text-gray-400 tabular-nums w-4">{m.opponentVotes}</span>
                        </div>
                      )}
                      {m.commentary && <p className="mt-2 text-[11px] italic text-gray-500 leading-snug">"{m.commentary}"</p>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {!refreshing && !n.meaning && !n.notes && n.matchHistory.length === 0 && (
            <p className="text-gray-400 text-sm text-center py-4">No details yet for this name.</p>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main export ───────────────────────────────────────────────────────────────

export default function NameCardGrid({
  names,
  lckRequired,
  compact = false,
  userVotedIds,
  futuresOddsMap,
  userBalance,
  myFuturesBets,
  highlightId,
}: {
  names: NameData[];
  lckRequired: number;
  compact?: boolean;
  userVotedIds?: Set<string>;
  // Optional: passed from roster page to enable in-card futures betting.
  // Keys are nameId. When absent (bracket page), betting UI is hidden.
  futuresOddsMap?: Record<string, number>;
  userBalance?: number;
  myFuturesBets?: Record<string, { wager: number; odds: number }>;
  // Optional: a nameId to auto-open the detail/bet modal for on mount — the
  // deep-link target from global search (search has no betting/odds data of
  // its own, so it routes here instead of duplicating PlayerCard/EliminatedNameModal).
  highlightId?: string;
}) {
  const router = useRouter();
  const [selected, setSelected] = useState<NameData | null>(
    highlightId ? names.find((n) => n.id === highlightId) ?? null : null
  );

  // Closing a card revalidates the underlying page's server data (sort order,
  // eligible/resurrected sections, updated counts on OTHER cards) — the list
  // itself is a server-render snapshot that otherwise never refreshes for as
  // long as the page/PWA tab stays open.
  function closeCard() {
    setSelected(null);
    router.refresh();
  }

  return (
    <>
      {names.map((n) => {
        const total  = n.winCount + n.lossCount + n.tieCount;
        const winPct = total > 0 ? Math.round((n.winCount / total) * 100) : null;
        const isElim = n.eliminated && !n.resurrected;
        const isLckEligible = n.lcKEligible && n.lckOpen && !n.resurrected;
        const pad      = compact ? "p-2" : "p-3";
        const nameSize = compact ? "text-xs" : "text-sm";

        return (
          <button
            key={n.id}
            onClick={() => setSelected(n)}
            className={`w-full text-left rounded-2xl border shadow-sm transition hover:shadow-md hover:border-purple-300 cursor-pointer select-none ${pad} ${
              n.status === "RESURRECTED"
                ? "bg-orange-50 border-orange-200"
                : isElim
                ? "bg-white/50 border-purple-50 opacity-60"
                : "bg-white border-purple-100"
            }`}
            style={{ WebkitUserSelect: "none", userSelect: "none" }}
          >
            <div className="flex items-start justify-between gap-1">
              <span className={`font-black leading-tight truncate ${nameSize} ${isElim ? "text-gray-400 line-through" : "text-purple-800"}`}>
                {n.name}
              </span>
              <span className="shrink-0 text-[10px] leading-tight mt-px">
                {n.status === "RESURRECTED" ? "🔥" :
                 isLckEligible             ? "🍳" :
                 n.tieCount > 0            ? "⚡" : ""}
              </span>
            </div>
            <div className="flex items-center gap-1 mt-0.5 flex-wrap">
              <span className={`tabular-nums ${compact ? "text-[9px]" : "text-[11px]"} text-purple-400`}>
                <span className="text-green-600 font-bold">{n.winCount}</span>
                {n.tieCount > 0 && (<><span className="text-purple-200">–</span><span className="text-yellow-500 font-bold">{n.tieCount}</span></>)}
                <span className="text-purple-200">–</span>
                <span className="text-red-400 font-bold">{n.lossCount}</span>
              </span>
              {winPct !== null && !compact && <span className="text-[10px] text-purple-300">{winPct}%</span>}
              {n.winStreak >= 2 && <span className={`ml-auto font-black text-orange-500 ${compact ? "text-[9px]" : "text-[10px]"}`}>W{n.winStreak}</span>}
            </div>
          </button>
        );
      })}

      {selected && (
        selected.eliminated && !selected.resurrected ? (
          <EliminatedNameModal
            name={selected}
            lckRequired={lckRequired}
            alreadyVoted={userVotedIds?.has(selected.id) ?? false}
            onClose={closeCard}
          />
        ) : (
          <PlayerCard
            initial={selected}
            onClose={closeCard}
            lckRequired={lckRequired}
            futuresOdds={futuresOddsMap?.[selected.id]}
            userBalance={userBalance}
            existingBet={myFuturesBets?.[selected.id] ?? null}
          />
        )
      )}
    </>
  );
}
